/**
 * LeadAgeIndicator - Visual urgency indicator based on lead age
 *
 * Shows how old a lead is with color coding:
 * - Green: < 1 hour (fresh, needs immediate contact)
 * - Yellow: 1-24 hours (aging, should be contacted)
 * - Red: > 24 hours (stale, needs attention)
 */

import { cn } from '@limo/shared'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Clock } from 'lucide-react'

interface LeadAgeIndicatorProps {
  createdAt: string
  firstContactedAt?: string | null
  className?: string
  showIcon?: boolean
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Format relative time for display
 */
function formatRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format full date/time for tooltip
 */
function formatFullDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Get age level based on hours since creation
 */
function getAgeLevel(createdAt: string): {
  level: 'fresh' | 'aging' | 'stale'
  color: string
  bgColor: string
  borderColor: string
  dotColor: string
  description: string
} {
  const now = new Date()
  const date = new Date(createdAt)
  const diffHours = (now.getTime() - date.getTime()) / 3600000

  if (diffHours < 1) {
    return {
      level: 'fresh',
      color: 'text-green-700',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      dotColor: 'bg-green-500',
      description: 'Fresh lead - contact immediately',
    }
  } else if (diffHours < 24) {
    return {
      level: 'aging',
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      dotColor: 'bg-amber-500',
      description: 'Aging lead - should be contacted today',
    }
  } else {
    return {
      level: 'stale',
      color: 'text-red-700',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      dotColor: 'bg-red-500',
      description: 'Stale lead - needs immediate attention',
    }
  }
}

export function LeadAgeIndicator({
  createdAt,
  firstContactedAt,
  className,
  showIcon = false,
  showLabel = true,
  size = 'md',
}: LeadAgeIndicatorProps) {
  const relativeTime = formatRelativeTime(createdAt)
  const fullDateTime = formatFullDateTime(createdAt)

  // If already contacted, show neutral styling
  const isContacted = !!firstContactedAt
  const ageLevel = isContacted ? null : getAgeLevel(createdAt)

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5',
  }

  const dotSizeClasses = {
    sm: 'h-1.5 w-1.5',
    md: 'h-2 w-2',
    lg: 'h-2.5 w-2.5',
  }

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  }

  // If contacted, show simple neutral indicator
  if (isContacted) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 text-muted-foreground',
                sizeClasses[size],
                className
              )}
            >
              {showIcon && <Clock className={iconSizeClasses[size]} />}
              {showLabel && relativeTime}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">Created: {fullDateTime}</p>
            <p className="text-xs text-muted-foreground mt-1">
              First contacted: {formatFullDateTime(firstContactedAt)}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // Not contacted - show urgency indicator
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border',
              sizeClasses[size],
              ageLevel?.bgColor,
              ageLevel?.borderColor,
              ageLevel?.color,
              className
            )}
          >
            <span
              className={cn(
                'rounded-full animate-pulse',
                dotSizeClasses[size],
                ageLevel?.dotColor
              )}
            />
            {showIcon && <Clock className={iconSizeClasses[size]} />}
            {showLabel && relativeTime}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm font-medium">{ageLevel?.description}</p>
          <p className="text-xs text-muted-foreground mt-1">{fullDateTime}</p>
          <p className="text-xs text-amber-600 mt-1">Not yet contacted</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * Simpler dot-only indicator for use in table rows
 */
export function LeadAgeDot({
  createdAt,
  firstContactedAt,
  className,
}: {
  createdAt: string
  firstContactedAt?: string | null
  className?: string
}) {
  const isContacted = !!firstContactedAt
  const ageLevel = isContacted ? null : getAgeLevel(createdAt)

  if (isContacted) {
    return null // No indicator if contacted
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-block h-2 w-2 rounded-full animate-pulse',
              ageLevel?.dotColor,
              className
            )}
          />
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{ageLevel?.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
